import React, { useState } from "react";
import { Box, Button, IconButton, Stack, Typography, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { LeadThread } from "./leadApi";
import { getNextAction, NAVY } from "./leadCellUtils";
import { useContactActions } from "../../Components/ContactActions";

interface Props {
  lead:    LeadThread;
  /** Unused — kept for prop-compat with the row (was used for hover-fade). */
  isHovered?: boolean;
  onNote:  () => void;
}

/**
 * Strip a leading emoji + space from a NextActionDef label so the
 * button can render an MUI icon instead. `getNextAction()` still
 * carries the emoji for backward compat with other callers.
 */
function cleanLabel(label: string): string {
  return label.replace(/^[\p{Emoji}☀-➿️]+\s*/u, "").trim();
}

/**
 * Pick an outline MUI icon based on which action label the smart-action
 * resolver returned. Keeps NextActionCell decoupled from the literal
 * emoji strings while still matching the reference design's visual
 * cue (warning / phone / bolt / clipboard / check / chat).
 */
function actionIcon(label: string): React.ReactNode {
  const l = label.toLowerCase();
  if (l.includes("urgent follow")) return <BoltOutlinedIcon         sx={{ fontSize: 14 }} />;
  if (l.includes("contact immed")) return <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />;
  if (l.includes("call"))          return <PhoneOutlinedIcon        sx={{ fontSize: 14 }} />;
  if (l.includes("prepare quote")) return <AssignmentOutlinedIcon   sx={{ fontSize: 14 }} />;
  if (l.includes("send quote"))    return <RequestQuoteOutlinedIcon sx={{ fontSize: 14 }} />;
  if (l.includes("confirm"))       return <CheckCircleOutlineIcon   sx={{ fontSize: 14 }} />;
  if (l.includes("follow up"))     return <ChatBubbleOutlineIcon    sx={{ fontSize: 14 }} />;
  if (l.includes("no action"))     return <CheckCircleOutlineIcon   sx={{ fontSize: 14 }} />;
  return null;
}

export const NextActionCell = React.memo(function NextActionCell({ lead, onNote }: Props) {
  const action = getNextAction(lead);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { handleCallAction, handleEmailAction, modalElement } = useContactActions();

  return (
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
      {/* ── Primary action button ─────────────────────────────────
          Black/navy filled for urgent; white outline for normal.
          The icon is an MUI outline shape (not an emoji) so the row
          reads as enterprise CRM. */}
      {action ? (() => {
        const label = cleanLabel(action.label);
        const icon  = actionIcon(action.label);
        const urgent = !!action.urgent;
        // Dispatch the primary action by inspecting the resolver's
        // label. Call-flavored labels (Call Now / Follow Up / Contact
        // Immediately / Urgent Follow-up) trigger the OS dialer via
        // the shared handleCallAction; "Send Quote" / "Prepare Quote"
        // open the email compose modal pre-filled to the lead.
        // Anything else (Confirm Booking / No Action Needed) is left
        // alone — we let the click bubble to the row so the lead
        // drawer opens, which is the correct next surface.
        const lower = (action.label || "").toLowerCase();
        const isCallAction  = lower.includes("call") || lower.includes("contact immed")
                              || lower.includes("urgent follow") || lower.includes("follow up");
        const isEmailAction = lower.includes("send quote") || lower.includes("prepare quote");
        const handlePrimary = (e: React.MouseEvent) => {
          if (isCallAction) {
            e.stopPropagation();
            handleCallAction(lead.phone);
          } else if (isEmailAction) {
            e.stopPropagation();
            handleEmailAction({ email: lead.email, name: lead.name, leadId: lead.id });
          }
          // No else — let propagation reach the row so the drawer opens.
        };
        return (
          <Button
            variant={urgent ? "contained" : "outlined"}
            size="small"
            disableElevation
            startIcon={icon}
            onClick={handlePrimary}
            sx={{
              flex: 1, minWidth: 0,
              textTransform: "none",
              fontWeight: 600, fontSize: 12,
              borderRadius: "8px",
              height: 30,
              px: 1.2,
              whiteSpace: "nowrap",
              ...(urgent
                ? {
                    bgcolor: NAVY, color: "#fff",
                    boxShadow: "0 1px 2px rgba(13,27,42,0.16)",
                    "&:hover": { bgcolor: "#1b2e44" },
                    "& .MuiButton-startIcon": { mr: 0.6, "& svg": { color: "#fff" } },
                  }
                : {
                    bgcolor: "#fff", color: "#1e293b",
                    borderColor: "#e2e8f0",
                    "&:hover": { bgcolor: "#fafbfc", borderColor: "#cbd5e1" },
                    "& .MuiButton-startIcon": { mr: 0.6, "& svg": { color: "#475569" } },
                  }
              ),
            }}
          >
            {label}
          </Button>
        );
      })() : (
        <Typography sx={{ flex: 1, color: "#cbd5e1", fontSize: "0.72rem" }}>—</Typography>
      )}

      {/* ── Overflow menu (⋮) — call / email / add note ─────────── */}
      <Tooltip title="More actions">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
          sx={{
            width: 26, height: 26, flexShrink: 0,
            color: "#94a3b8",
            "&:hover": { color: NAVY, bgcolor: "#f1f5f9" },
          }}
        >
          <MoreVertIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={(e: any) => { e?.stopPropagation?.(); setMenuAnchor(null); }}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5, minWidth: 180,
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 8px 24px rgba(13,27,42,0.10)",
            },
          },
        }}
      >
        {lead.phone && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(null);
              handleCallAction(lead.phone);
            }}
            sx={{ fontSize: 12.5, py: 0.8 }}
          >
            <ListItemIcon sx={{ minWidth: "28px !important" }}>
              <PhoneOutlinedIcon sx={{ fontSize: 16, color: "#475569" }} />
            </ListItemIcon>
            <ListItemText primary={`Call ${lead.phone}`} primaryTypographyProps={{ fontSize: 12.5, fontWeight: 500 }} />
          </MenuItem>
        )}
        {lead.email && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(null);
              // Hand off to the shared compose modal — no mailto:.
              // Keeps the send inside the CRM so it's threaded,
              // templated, and recorded in the activity timeline.
              handleEmailAction({ email: lead.email, name: lead.name });
            }}
            sx={{ fontSize: 12.5, py: 0.8 }}
          >
            <ListItemIcon sx={{ minWidth: "28px !important" }}>
              <EmailOutlinedIcon sx={{ fontSize: 16, color: "#475569" }} />
            </ListItemIcon>
            <ListItemText
              primary="Send email"
              secondary={lead.email}
              primaryTypographyProps={{ fontSize: 12.5, fontWeight: 500 }}
              secondaryTypographyProps={{ fontSize: 10.5, color: "#94a3b8" }}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => { e.stopPropagation(); setMenuAnchor(null); onNote(); }}
          sx={{ fontSize: 12.5, py: 0.8 }}
        >
          <ListItemIcon sx={{ minWidth: "28px !important" }}>
            <NoteAddOutlinedIcon sx={{ fontSize: 16, color: "#475569" }} />
          </ListItemIcon>
          <ListItemText primary="Add note" primaryTypographyProps={{ fontSize: 12.5, fontWeight: 500 }} />
        </MenuItem>
        <Box sx={{ borderTop: "1px solid #f1f5f9", my: 0.4 }} />
        <MenuItem
          disabled
          sx={{ fontSize: 11, color: "#94a3b8", py: 0.4 }}
        >
          More actions in the lead drawer
        </MenuItem>
      </Menu>
      {/* Shared compose modal — rendered lazily by MUI Dialog (open=false
          is cheap), so it's fine to mount one per cell. */}
      {modalElement}
    </Stack>
  );
});
